import React from "react";
import {IconButton, LinearProgress} from "@mui/material";
import {Box, Colors, IconApproved, Text} from "@bounce/shared/ui";
import {IconAlertCircle, IconRefresh} from "../assets";
import {Tooltip} from "../ui/components";

const STATUS_ICON_TILE_SIZE = 36;
const STATUS_ICON_SIZE = 20;
const REFRESH_ICON_SIZE = 16;
const PROGRESS_BAR_HEIGHT = 6;

const spinSx = {
  "@keyframes spin": {from: {transform: "rotate(0deg)"}, to: {transform: "rotate(360deg)"}},
  animation: "spin 1.2s linear infinite",
};

export const parseUtc = (timestamp: string) =>
  new Date(/[Z+]|-\d\d:\d\d$/.test(timestamp) ? timestamp : `${timestamp}Z`);

type Props = {
  title: string;
  subtitle: string;
  isRunning: boolean;
  isWarning?: boolean;
  progressPercent?: number;
  refreshLabel: string;
  onRefresh: () => void;
  isRefreshing: boolean;
};

export const StatusBanner = ({
  title,
  subtitle,
  isRunning,
  isWarning = false,
  progressPercent,
  refreshLabel,
  onRefresh,
  isRefreshing,
}: Props) => (
  <Box
    sx={{
      mb: 2,
      px: 2,
      py: 1.5,
      borderRadius: "12px",
      border: `1px solid ${Colors.tertiary._300}`,
      backgroundColor: Colors.tertiary._100,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
    }}
  >
    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
      <Box
        sx={{
          width: STATUS_ICON_TILE_SIZE,
          height: STATUS_ICON_TILE_SIZE,
          borderRadius: "8px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isRunning ? Colors.blue._100 : isWarning ? Colors.yellow._200 : Colors.green._100,
          color: isRunning ? Colors.blue._800 : isWarning ? Colors.yellow._900 : Colors.green._800,
        }}
      >
        {isRunning ? (
          <Box sx={{display: "flex", ...spinSx}}>
            <IconRefresh width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
          </Box>
        ) : isWarning ? (
          <IconAlertCircle width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
        ) : (
          <IconApproved width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
        )}
      </Box>
      <Box sx={{display: "flex", flexDirection: "column", gap: 0.25, flex: 1, minWidth: 0}}>
        <Text size={14} variant="inter600" sx={{color: Colors.grey._1000}}>
          {title}
        </Text>
        <Text size={12} sx={{color: Colors.grey._700}}>
          {subtitle}
        </Text>
      </Box>
      <Tooltip title={refreshLabel} placement="top">
        <span>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label={refreshLabel}
            sx={{
              borderRadius: "8px",
              color: Colors.grey._700,
              "&:hover": {backgroundColor: Colors.tertiary._200, color: Colors.grey._1000},
            }}
          >
            <Box sx={{display: "flex", ...(isRefreshing && spinSx)}}>
              <IconRefresh width={REFRESH_ICON_SIZE} height={REFRESH_ICON_SIZE} />
            </Box>
          </IconButton>
        </span>
      </Tooltip>
    </Box>
    {isRunning && progressPercent !== undefined && (
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        sx={{
          height: PROGRESS_BAR_HEIGHT,
          borderRadius: PROGRESS_BAR_HEIGHT / 2,
          backgroundColor: Colors.blue._100,
          "& .MuiLinearProgress-bar": {backgroundColor: Colors.blue._800, borderRadius: PROGRESS_BAR_HEIGHT / 2},
        }}
      />
    )}
  </Box>
);
